import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useUpdatedState} from 'helpers/hooks/utils';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {DragHandleContext, useDragHandle} from 'components/organisms/Utils/DragDrop/DndContext';
import Box from 'components/atoms/Layout/Box/Box';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import {DragIndicator} from '@mui/icons-material';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Delete from '@mui/icons-material/Delete';
import StyledTagForm from 'components/organisms/Forms/TagForm/TagForm.styles';

const TagForm = React.forwardRef((props, ref) => {
  const {
    tag,
    only,
    required,
    readOnly,
    disabled,
    removable,
    error,
    errorDup,
    hasPoints,
    autoTagType,
    hideActions,
    onChange,
    onError,
    onDelete,
    ...innerProps
  } = useComponentProps({
    elevation: 1,
    radius: 'round',
    ...props
  }, 'TagForm');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  const tagForm = useMemo(() => ({
    refs: {
      ref: innerRef,
      formRef
    },
    submit: (touch = true) => {
      return formRef.current?.submit(touch);
    },
    validate: (touch = true) => {
      return formRef.current?.validate(touch);
    },
    reset: (state) => {
      setIsDirty(false);
      return formRef.current?.reset(state);
    }
  }), []);

  useImperativeHandle(ref, () => tagForm);

  const [isDirty, setIsDirty] = useState(false);
  const [internalValue] = useUpdatedState({...tag, ...tag?.autoTagParams});

  const isNew = +internalValue?.tagId < 0;

  const dragHandleContext = useDragHandle(DragHandleContext);

  useEffect(() => {
    if (required && error) {
      return utils.observeTimeout(() => {
        try {
          formRef.current?.validate(true);
        } catch (e) {
          /* SQUASH */
        }
      }, constants.debounce.minimal)
    }
  }, [required, error]);

  const fields = useMemo(() => {
    const fields = [];

    fields.push({
      name: 'value',
      label: 'Name',
      type: constants.formFieldTypes.text,
      format: constants.formFieldFormatTypes.lower,
      validation: constants.formFieldValidationTypes.tag,
      entity: 'tagGroup',
      formGroup: 'header',
      initial: internalValue.value,
      readOnly: readOnly,
      required: required || isDirty,
      disabled: disabled,
      FormFieldProps: {
        error: errorDup ? 'Duplicate name' : null,
        variant: 'inlineLabel',
        size: 'smaller',
        hiddenLabel: true
      }
    });

    if (hasPoints) {
      fields.push({
        name: 'points',
        label: 'Points',
        type: constants.formFieldTypes.text,
        validation: [constants.formFieldValidationTypes.int, constants.formFieldValidationTypes.nonNegative,
          `${constants.formFieldValidationTypes.min}(-1000)`, `${constants.formFieldValidationTypes.max}(1000)`],
        format: constants.formFieldFormatTypes.int,
        postfix: 'points',
        entity: 'tagGroup',
        formGroup: 'header',
        initial: internalValue.points ?? 0,
        readOnly: readOnly,
        required: true,
        disabled: disabled,
        FormFieldProps: {
          variant: 'inlineLabel',
          size: 'smaller',
          fullWidth: false,
          hiddenLabel: true,
          hiddenPlaceholder: true
        }
      });
    }

    if ([constants.collection.autoTagTypes.byAI, constants.collection.autoTagTypes.byAIOnline].includes(autoTagType)) {
      fields.push({
        name: 'description',
        label: 'Description',
        placeholder: 'Describe the tag',
        type: constants.formFieldTypes.textarea,
        initial: internalValue.description ?? '',
        formGroup: 'body',
        entity: 'tagGroup',
        FormFieldProps: {
          hiddenLabel: false,
          size: 'smaller',
          minRows: 1
        }
      })
    } else if (autoTagType === constants.collection.autoTagTypes.byKeywords) {
      fields.push({
        name: 'keywords',
        label: 'Keywords',
        type: constants.formFieldTypes.autocomplete,
        validation: constants.formFieldValidationTypes.list,
        conversion: constants.formFieldConversionTypes.label,
        initial: internalValue.keywords ?? [],
        formGroup: 'body',
        entity: 'tagGroup',
        FormFieldProps: {
          multiple: true,
          createOption: true,
          openDirect: false,
          openOnFocus: false,
          hiddenLabel: false,
          size: 'smaller',
          TagProps: {
            variant: 'filled'
          },
          ChipProps: {
            variant: 'filled'
          },
          ChipListProps: {
            variant: 'standard'
          }
        }
      })
    }

    return fields;
  }, [internalValue, errorDup, hasPoints, autoTagType, readOnly, disabled, required, isDirty]);

  const onChangeEvent = useEffectEvent(onChange);
  const handleSubmit = useCallback((values, actions) => {
    if ([constants.collection.autoTagTypes.byAI, constants.collection.autoTagTypes.byAIOnline].includes(autoTagType)) {
      values.autoTagParams = { description: values.description ?? '' };
      delete values.description;
    } else if (autoTagType === constants.collection.autoTagTypes.byKeywords) {
      values.autoTagParams = { keywords: values.keywords ?? [] };
      delete values.keywords;
    } else {
      values.autoTagParams = null;
    }

    utils.asPromise(onChangeEvent)(values)
      .finally(() => {
        actions.setSubmitting(false);
      });
  }, [autoTagType, onChangeEvent]);

  const handleChange = useCallback(() => {
    formRef.current?.submit(false);
  }, []);

  const handleError = useCallback((field, value) => {
    utils.asPromise(onChangeEvent)({...internalValue, [field.name]: value});
  }, [internalValue, onChangeEvent]);

  const onErrorEvent = useEffectEvent(onError);
  const handleValidating = useCallback((validating, dirty, errors) => {
    setIsDirty((current) => current || dirty || errors);
    onErrorEvent?.(errors);
  }, [onErrorEvent]);

  const dragAction = useMemo(() => ({
    icon: <Icon icon={DragIndicator} size="smaller" />,
    tooltip: 'Move tag',
    TooltipProps: dragHandleContext?.isDragging ? {
      open: false
    } : null,
    IconButtonProps: {
      ...utils.filterObject(dragHandleContext, 'isDragging'),
      disabled: (disabled || readOnly || !removable),
      onPointerDown: (e) => {
        if (disabled || readOnly || !removable) {
          e.preventDefault();
        } else {
          dragHandleContext?.onPointerDown?.(e);
        }
      }
    },
    onClick: () => null
  }), [disabled, readOnly, removable, dragHandleContext]);

  const onDeleteEvent = useEffectEvent(onDelete);
  const deleteAction = useMemo(() => ({
    label: 'Remove tag',
    tooltip: 'Remove tag',
    icon: Delete,
    ActionIconButtonProps: {
      IconProps: {
        size: 'smaller'
      }
    },
    IconButtonProps: {
      disabled: (disabled || readOnly || !removable || (only && isNew && !isDirty))
    },
    onClick: (e) => {
      onDeleteEvent?.(e);
    }
  }), [disabled, readOnly, removable, only, isDirty, isNew, onDeleteEvent]);

  const renderFormContent = useCallback((groups) => {
    const header = groups.find((g) => g.name === 'header');
    const body = groups.find((g) => g.name === 'body');

    const renderActions = () => {
      return <Box className="TagForm-actions">
        {dragHandleContext ? <ActionIconButton action={dragAction}
                                               disableRipple={true}
                                               size="smaller"
                                               density="sparse"
                                               variant="outlined"/> : null}
        <ActionIconButton size="smaller"
                          density="sparse"
                          variant="contained"
                          color="error"
                          action={deleteAction}/>
      </Box>
    }

    return <React.Fragment>
      <Box className="TagForm-header">
        <Box className="Form-fields">
          {header.rendered}
        </Box>
        {!hideActions ? renderActions() : null}
      </Box>
      {body ? <Box className="TagForm-body">
        <Box className="Form-fields">
          {body.rendered}
        </Box>
      </Box> : null}
    </React.Fragment>
  }, [deleteAction, dragAction, dragHandleContext, hideActions]);

  return <StyledTagForm ref={innerRef} {...innerProps}>
    <InlineForm ref={formRef}
                className="TagForm-form"
                autoFocus={isNew && !only}
                onSubmit={handleSubmit}
                onChange={handleChange}
                onError={handleError}
                onValidating={handleValidating}
                renderContent={renderFormContent}
                fields={fields}/>
  </StyledTagForm>
});

TagForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  tag: PropTypes.object,
  only: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  removable: PropTypes.bool,
  hideActions: PropTypes.bool,
  error: PropTypes.bool,
  errorDup: PropTypes.bool,
  hasPoints: PropTypes.bool,
  autoTagType: PropTypes.string,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func
};

export default TagForm;
